import { Empty, Skeleton, theme } from 'antd'
import type { ReactNode } from 'react'

import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { classNames } from '@/Ship/Helpers/classNames'

import styles from './Overview.module.css'

/** Знак базової валюти тенанта (гривня) — гроші огляду приходять у ній. */
export const CURRENCY_SIGN = '₴'

const numberFormat = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 })
const shareFormat = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 })

export const formatInt = (value: number): string => numberFormat.format(value)

export const formatMoney = (value: number): string => `${numberFormat.format(value)} ${CURRENCY_SIGN}`

export const formatShare = (value: number | null): string => (value === null ? '—' : `${shareFormat.format(value)} %`)

/** Короткі гроші для осі й центру кільця: «1,8 млн ₴», «608 тис ₴». */
export const formatMoneyShort = (value: number): string => {
    if (Math.abs(value) >= 1_000_000)
        return `${shareFormat.format(Math.round(value / 100_000) / 10)} млн ${CURRENCY_SIGN}`
    if (Math.abs(value) >= 10_000) return `${numberFormat.format(Math.round(value / 1_000))} тис ${CURRENCY_SIGN}`

    return formatMoney(value)
}

const MONTHS_SHORT = ['січ', 'лют', 'бер', 'кві', 'тра', 'чер', 'лип', 'сер', 'вер', 'жов', 'лис', 'гру']

/** Підпис місяця на осі: «жов» або «січ 26» на зламі року. */
export const monthLabel = (year: number, month: number, withYear = false): string =>
    `${MONTHS_SHORT[month - 1] ?? ''}${withYear ? ` ${String(year).slice(2)}` : ''}`

/** Дата `РРРР-ММ-ДД` → «22.10». */
export const shortDate = (iso: string): string => (iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso)

/**
 * Категоріальна палітра огляду — лише токени теми (пресети antd), у
 * фіксованому порядку, перевіреному валідатором палітр на розрізнення при
 * дальтонізмі: синій, помаранчевий, бірюзовий, фіолетовий, зелений,
 * малиновий.
 *
 * ⚠️ Сірого тут немає навмисно: він читається як «немає даних»
 * (`development.md`, рядок вартових «Графік читабельний»). Нейтральний
 * колір дістається лише залишковій частці «Інші» — {@see useOtherColor}.
 * Бренд (`colorPrimary`) у палітру серій не входить: він акцент каркаса, а
 * в Астеріксі ще й червоний — читався б як тривога.
 */
export const useChartPalette = (): string[] => {
    const { token } = theme.useToken()
    const fallback = [token.blue, token.orange, token.cyan, token.purple, token.green, token.magenta]

    // Бренд може мати власну гаму графіків (ADR 048: у Demo — помаранчева
    // головна серія). Недостачу добираємо стандартною, щоб індекси 0…5
    // існували завжди.
    return fallback.map((color, index) => BrandConfig.chartPalette?.[index] ?? color)
}

/**
 * Ролі кольорів серій: головна величина, порівняльні — з палітри бренду в
 * тому самому порядку, що й {@see useChartPalette}. Для Астерікса — рівно
 * попередні кольори (синій, помаранчевий, бірюзовий, фіолетовий, зелений,
 * малиновий).
 */
export const useChartColors = () => {
    const [primary, second, third, fourth, fifth, sixth] = useChartPalette()

    return { primary: primary!, second: second!, third: third!, fourth: fourth!, fifth: fifth!, sixth: sixth! }
}

/** Колір залишкової частки «Інші» — нейтральний токен заливки, не серія. */
export const useOtherColor = (): string => theme.useToken().token.colorTextQuaternary

interface PanelProps {
    title: string
    subtitle?: ReactNode
    extra?: ReactNode
    loading?: boolean
    /** Немає даних — замість тіла пояснення, а не порожня картка. */
    empty?: string | false
    block?: string
    children?: ReactNode
    skeletonRows?: number
}

/**
 * Блок огляду: заголовок, підзаголовок (вікно чисел), праворуч — що
 * показано («6 із 14»). Поки летить відповідь — скелет; немає даних —
 * `Empty` з поясненням, чому порожньо.
 */
export const Panel = ({ title, subtitle, extra, loading, empty, block, children, skeletonRows = 5 }: PanelProps) => (
    <section className={classNames(styles['panel'])} data-block={block}>
        <header className={classNames(styles['panelHead'])}>
            <div style={{ minWidth: 0 }}>
                <h3 className={classNames(styles['panelTitle'])}>{title}</h3>
                {subtitle ? <div className={classNames(styles['panelSubtitle'])}>{subtitle}</div> : null}
            </div>
            {extra && !loading ? <div className={classNames(styles['panelExtra'])}>{extra}</div> : null}
        </header>
        <div className={classNames(styles['panelBody'])}>
            {loading ? (
                <Skeleton active title={false} paragraph={{ rows: skeletonRows }} />
            ) : empty ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={empty} />
            ) : (
                children
            )}
        </div>
    </section>
)

/** «6 із 14» — обрізаний перелік називає, скільки приховав. */
export const shownOf = (shown: number, total: number, noun: string): string =>
    total > shown ? `${shown} із ${total} ${noun}` : `${total} ${noun}`

/** Підпис періоду рейтингів. */
export const PERIOD_LABELS = {
    month: 'Цей місяць',
    quarter: '3 місяці',
    year: '12 місяців',
} as const
